/**
 * Which columns a schedule or a timeline reads, and how wide its columns are.
 *
 * Both view types draw nothing until they are told -- a schedule needs one
 * date column, a timeline needs two.
 *
 * The two ends of a timeline must agree about time and timezone. The backend
 * refuses a disagreeing pair, but only when both arrive in the same request,
 * and each of these lists sends one -- so the disagreeing columns are left out
 * of the list rather than refused after being picked, and the menu says why
 * when it has left any out.
 */
import { CalendarRange } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Field, ViewOf } from '@/api/types'
import { Button } from '@/shared/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import type { DateFieldLike } from '../fields/dates'
import { TIMESCALE_COLUMNS, type Timescale } from '../gantt/timeline'
import { datesAgree, dateFields } from '../views/date-fields'
import { FieldChoice } from './field-choice'
import { useViewSettings, type ViewSettings } from './view-settings-mutations'

const TIMESCALES = Object.keys(TIMESCALE_COLUMNS) as Timescale[]

export function DateFieldsMenu({
  tableId,
  view,
  fields,
}: {
  tableId: number
  view: ViewOf<'schedule'> | ViewOf<'gantt'>
  fields: readonly Field[]
}) {
  const { t } = useTranslation()
  const { choose } = useViewSettings(tableId, view.id)
  const candidates = dateFields(fields)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5">
          <CalendarRange className="size-4 opacity-70" />
          {t('view.dates')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        {candidates.length === 0 ? (
          <DropdownMenuItem disabled>{t('view.datesNoFields')}</DropdownMenuItem>
        ) : view.type === 'schedule' ? (
          <FieldChoice
            label={t('view.dateField')}
            chosen={view.date_field ?? null}
            candidates={candidates}
            onChoose={(id) => {
              choose({ date_field: id })
            }}
          />
        ) : (
          <GanttChoices view={view} candidates={candidates} onChoose={choose} />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** A timeline's two ends and the width of its columns. */
function GanttChoices({
  view,
  candidates,
  onChoose,
}: {
  view: ViewOf<'gantt'>
  candidates: readonly Field[]
  onChoose: (settings: ViewSettings) => void
}) {
  const { t } = useTranslation()
  const start = candidates.find((field) => field.id === view.start_date_field) ?? null
  const end = candidates.find((field) => field.id === view.end_date_field) ?? null

  /** The columns that could be this end, given whatever the other one is. */
  const agreeingWith = (other: Field | null): Field[] =>
    other === null
      ? [...candidates]
      : candidates.filter((field) =>
          datesAgree(field as DateFieldLike, other as DateFieldLike),
        )

  const starts = agreeingWith(end)
  const ends = agreeingWith(start)
  const omitted = starts.length < candidates.length || ends.length < candidates.length

  return (
    <>
      <FieldChoice
        label={t('view.startDateField')}
        chosen={view.start_date_field ?? null}
        candidates={starts}
        onChoose={(id) => {
          onChoose({ start_date_field: id })
        }}
      />
      <DropdownMenuSeparator />
      <FieldChoice
        label={t('view.endDateField')}
        chosen={view.end_date_field ?? null}
        candidates={ends}
        onChoose={(id) => {
          onChoose({ end_date_field: id })
        }}
      />
      {omitted && (
        <p className="text-muted-foreground px-2 py-1.5 text-xs">
          {t('view.datesMustAgree')}
        </p>
      )}
      <DropdownMenuSeparator />
      <DropdownMenuLabel>{t('view.timescale')}</DropdownMenuLabel>
      <DropdownMenuRadioGroup
        value={view.timescale ?? 'day'}
        onValueChange={(value) => {
          onChoose({ timescale: value as Timescale })
        }}
      >
        {TIMESCALES.map((timescale) => (
          <DropdownMenuRadioItem key={timescale} value={timescale}>
            {t(`view.timescale_${timescale}`)}
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </>
  )
}
